// MyTasks. Kanban + list dual view, scoped to the current user's tasks.
// Lane B adds: mobile-first layout, dependency hint for blocked tasks,
// recurring indicator next to due date.

import { useState, useMemo } from 'react';
import { isOverdue } from '../lib/dates.ts';
import { isResponsible } from '../lib/owners.ts';
import { loadViewMode, saveViewMode } from '../lib/storage.js';
import { navigate } from '../lib/routing.js';
import { PageHeader } from '../components/PageHeader.jsx';
import { TaskCard } from '../components/TaskCard.jsx';
import { ListView } from '../components/ListView.jsx';
import { Icon } from '../components/Icon.jsx';
import { WeeklyWidget } from '../components/WeeklyWidget.jsx';

export const MyTasks = ({ user, tasks, staff = [], onCreate }) => {
  const [viewMode, setViewMode] = useState(loadViewMode());
  const [filter, setFilter] = useState('全部');
  // "My tasks" = tasks where I'm the R (Responsible). Tasks where I'm
  // C/I show up under a separate filter in a future commit; for now
  // keeping the queue tight per the eng review "tasks on my plate" framing.
  const myTasks = tasks.filter(t => isResponsible(t, user.id));

  const bucketed = useMemo(() => ({
    '待开始': myTasks.filter(t => t.status === '待开始'),
    '进行中': myTasks.filter(t => t.status === '进行中'),
    '阻塞':   myTasks.filter(t => t.status === '阻塞'),
    '已完成': myTasks.filter(t => t.status === '已完成')
  }), [myTasks]);

  const stats = {
    total: myTasks.length,
    done: bucketed['已完成'].length,
    inprog: bucketed['进行中'].length,
    blocked: bucketed['阻塞'].length,
    overdue: myTasks.filter(isOverdue).length
  };

  function switchView(m) { setViewMode(m); saveViewMode(m); }
  function openTask(id) { navigate('/task/' + id); }

  if (myTasks.length === 0) {
    return (
      <>
        <div className="vava-page-header">
          <div>
            <h1 className="vava-page-title">我的待办</h1>
            <div className="vava-page-meta">欢迎 · {user.name}</div>
          </div>
          {onCreate && (
            <button className="btn btn-primary" onClick={onCreate}>
              <Icon name="plus"/>新建待办
            </button>
          )}
        </div>
        <div className="vava-card empty-state">
          <div className="emoji">☀️</div>
          <div className="title">你还没有被分配待办</div>
          <div className="sub">点击"新建待办"派一个给自己或队友 · 也可以等 Regina 安排</div>
        </div>
      </>
    );
  }

  return (
    <>
      <WeeklyWidget />
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">我的待办</h1>
          <div className="vava-page-meta">
            本月 · {stats.done}/{stats.total} 完成
            {stats.overdue > 0 && <> · <span style={{ color: 'var(--vava-red)' }}>{stats.overdue} 逾期</span></>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="view-toggle" role="tablist" aria-label="视图切换">
            <button className={viewMode === 'kanban' ? 'active' : ''} onClick={() => switchView('kanban')} role="tab" aria-selected={viewMode === 'kanban'}>看板</button>
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => switchView('list')} role="tab" aria-selected={viewMode === 'list'}>列表</button>
          </div>
          {onCreate && (
            <button className="btn btn-primary" onClick={onCreate}>
              <Icon name="plus"/>新建待办
            </button>
          )}
        </div>
      </div>

      <div className="kpi-strip" aria-label="进度统计">
        <div className="kpi-item"><span className="kpi-dot" style={{ background: '#a1a1a1' }}/><span className="n">{bucketed['待开始'].length}</span><span className="l">待开始</span></div>
        <div className="kpi-item"><span className="kpi-dot" style={{ background: '#3b82f6' }}/><span className="n">{bucketed['进行中'].length}</span><span className="l">进行中</span></div>
        <div className="kpi-item"><span className="kpi-dot" style={{ background: 'var(--vava-red)' }}/><span className="n">{bucketed['阻塞'].length}</span><span className="l">阻塞</span></div>
        <div className="kpi-item"><span className="kpi-dot" style={{ background: 'var(--vava-green)' }}/><span className="n">{stats.done}</span><span className="l">已完成</span></div>
      </div>

      {viewMode === 'kanban' ? (
        <div className="kanban">
          {['待开始', '进行中', '阻塞', '已完成'].map(col => (
            <section className="kanban-col" key={col} role="region" aria-label={`${col} 待办`}>
              <header className="kanban-col-header">
                <div className="kanban-col-title">{col}</div>
                <span className="kanban-col-count">{bucketed[col].length}</span>
              </header>
              <div className="kanban-cards">
                {bucketed[col].length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--vava-text-muted)', padding: 12, textAlign: 'center' }}>
                    {col === '已完成' ? '本月还没完成任何待办' : col === '阻塞' ? '无阻塞' : '空'}
                  </div>
                ) : bucketed[col].map(t => (
                  <TaskCard key={t.id} task={t} allTasks={tasks} onClick={() => openTask(t.id)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <ListView tasks={myTasks} filter={filter} setFilter={setFilter} openTask={openTask} staff={staff} />
      )}
    </>
  );
};
